import { fetch } from 'http-common';

import { PostData } from 'types';
import { Constant, ListType } from 'types/api/api.type';
import { CustomerAddressDto, CustomerData, CustomerDetailsDto } from 'types/api/customer.type';

import { SortItem } from 'components/Table/types';

type CustomerDataList = Omit<ListType, 'content'> & { content: CustomerData[] };

export type CustomerSearchResult = {
  id: number;
  customerCode: string;
  displayName: string;
  commercialZoneCode: string;
  status: string;
  customerTypeCode: string;
};

type CustomerSearchResultList = Omit<ListType, 'content'> & {
  content: CustomerSearchResult[];
};

/**
 * Envelope returned by every customer-mapper Customer endpoint —
 * `{id, customerId, status, timestamp, userId, customer: {...fields}}`. FE
 * consumers (useCustomerForm.ts, List.hooks.tsx, columns.data.tsx, ...)
 * already expect a flat `CustomerData`-shaped object (fields at the root,
 * plus `id`/`status`), so the envelope is unwrapped here, once, right after
 * the HTTP call — no consumer needs to know the wrapper exists.
 */
type CustomerRecordEnvelope = {
  customerId: string;
  status: string;
  timestamp: string;
  customer: Record<string, unknown>;
};

const flattenCustomer = (envelope: CustomerRecordEnvelope): CustomerData =>
  ({
    ...envelope.customer,
    id: envelope.customerId,
    status: envelope.status,
  } as CustomerData);

class CustomersDataService {
  private http = fetch();

  /** Customer Mapper CRUD/workflow — new NestJS backend. */
  private ep = '/v1/customer-mapper/customers';

  /**
   * Unrelated, read-only CRM/order-creation module (`src/customers/` on the
   * backend) — getOrderCustomers/searchCustomers/getAddresses/getDetails/
   * getGeoNames/getStructureTypes below all belong here, not to Customer
   * Mapper. Deliberately not touched by this migration beyond adding the new
   * structure-types/shop-constants reads.
   */
  private crmEp = '/v1/customers';

  public async getAll(
    params?: any, // eslint-disable-line
    page?: number,
    size?: number,
    sorting?: SortItem
  ) {
    const response = await this.http.get<Omit<ListType, 'content'> & { content: unknown[] }>(
      this.ep,
      {
        params: {
          code: params?.get('code') || undefined,
          companyName: params?.get('name') || undefined,
          zone: params?.get('zone') || undefined,
          status: params?.get('status') || undefined,
          page: page || 0,
          limit: size || 10,
          sort: sorting ? `${sorting.id},${sorting.desc ? 'desc' : 'asc'}` : undefined,
        },
      }
    );

    return {
      ...response,
      data: {
        ...response.data,
        content: (response.data.content as CustomerRecordEnvelope[]).map(flattenCustomer),
      },
    };
  }

  public async getOrderCustomers(zone: string) {
    return this.http.get<CustomerDataList>(`${this.crmEp}/order`, {
      params: {
        zone,
        size: 10000,
      },
    });
  }

  public async searchCustomers(
    q: string,
    zone?: string,
    page = 0,
    size = 1000,
    status?: string,
    orderable?: boolean
  ) {
    // `orderable` comes after `status`, never instead of it. true: only customers that can order and are
    // APPROVED (the pickers that choose the customer of an order). axios leaves out undefined params, so a
    // caller that does not pass it sends exactly what it sent before.
    return this.http.get<CustomerSearchResultList>(`${this.crmEp}/search`, {
      params: { q, zone, page, size, status, orderable },
    });
  }

  public async getAddresses(customerId: number, type?: string) {
    return this.http.get<CustomerAddressDto[]>(`${this.crmEp}/${customerId}/addresses`, {
      params: type ? { type } : undefined,
    });
  }

  public async getDetails(customerId: number) {
    return this.http.get<CustomerDetailsDto>(`${this.crmEp}/${customerId}/details`);
  }

  public async get(id: string) {
    const response = await this.http.get<CustomerRecordEnvelope>(`${this.ep}/${id}`);

    return { ...response, data: flattenCustomer(response.data) };
  }

  public async post(data: PostData) {
    const response = await this.http.post<CustomerRecordEnvelope>(this.ep, data);

    return { ...response, data: flattenCustomer(response.data) };
  }

  public async put(id: string, data: PostData) {
    const response = await this.http.put<CustomerRecordEnvelope>(`${this.ep}/${id}`, data);

    return { ...response, data: flattenCustomer(response.data) };
  }

  public async postSubmit(id: string) {
    const response = await this.http.post<CustomerRecordEnvelope>(`${this.ep}/${id}/submit`);

    return { ...response, data: flattenCustomer(response.data) };
  }

  public async getStructureTypes() {
    const response = await this.http.get<{ code: string; description: string }[]>(
      `${this.ep}/structure-types`
    );

    return {
      ...response,
      data: response.data.map(({ code, description }) => ({ key: code, value: description })),
    };
  }

  public async getShopConstants() {
    return this.http.get<Record<string, Constant[]>>(`${this.ep}/shop-constants`);
  }

  public async getGeoNames(params: {
    countryCode: string;
    postalCode?: string;
    adminCode?: string;
  }) {
    return this.http.get<any[]>(`${this.crmEp}/geonames`, { params }); // eslint-disable-line
  }

  public async accept(id: string) {
    const response = await this.http.post<CustomerRecordEnvelope>(`${this.ep}/${id}/accept`);

    return { ...response, data: flattenCustomer(response.data) };
  }

  public async refuse(id: string) {
    const response = await this.http.post<CustomerRecordEnvelope>(`${this.ep}/${id}/refuse`);

    return { ...response, data: flattenCustomer(response.data) };
  }
}

const customersDataService = new CustomersDataService();

export { customersDataService };
